import { Video, KeyRound, Wifi, Clock, Bell, Phone, Wrench, Network, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const needsButtons = [
  { label: "Ordenar horarios y evitar tardanzas", href: "/control-de-asistencia-para-empresas", icon: Clock },
  { label: "Controlar accesos y zonas", href: "/control-de-acceso-empresarial", icon: KeyRound },
  { label: "Tener evidencia y prevención", href: "/videovigilancia-empresarial", icon: Video },
  { label: "Conectividad estable", href: "/redes-empresariales", icon: Network },
];

const services = [
  {
    icon: Video,
    title: "Videovigilancia + Alarmas",
    description: "Monitoreo 24/7, evidencia clara y disuasión real.",
    href: "/videovigilancia-empresarial",
    highlighted: true
  },
  {
    icon: KeyRound,
    title: "Control de acceso",
    description: "Controla entradas, zonas y permisos con registros.",
    href: "/control-de-acceso-empresarial"
  },
  {
    icon: Clock,
    title: "Control de asistencia",
    description: "Ordena horarios, reduce abusos y obtén reportes.",
    href: "/control-de-asistencia-para-empresas"
  },
  {
    icon: Wifi,
    title: "Redes + WiFi",
    description: "Conectividad estable para operaciones sin fallas.",
    href: "/redes-empresariales"
  },
  {
    icon: Phone,
    title: "Telefonía",
    description: "Comunicación ordenada y profesional.",
    href: "/telefonia-empresarial"
  },
  {
    icon: Wrench,
    title: "Soporte continuo",
    description: "Mantenimiento para que todo siga funcionando.",
    href: "/soporte-y-mantenimiento"
  }
];

const NewServices = () => {
  const needsAnimation = useScrollAnimation({ threshold: 0.1 });
  const servicesAnimation = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="servicios" className="py-14 md:py-20 bg-muted/30 scroll-mt-20">
      <div className="container px-4 sm:px-6">
        {/* Header */}
        <div 
          ref={needsAnimation.ref as React.RefObject<HTMLDivElement>}
          className={cn(
            "text-center mb-10 sm:mb-12 transition-all duration-700",
            needsAnimation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
        >
          <span className="inline-block text-primary font-medium mb-3 uppercase tracking-wide text-sm">
            Servicios
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
            Lo que resolvemos
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            Soluciones integrales para empresas que necesitan resultados, no promesas.
          </p>
        </div>

        {/* Quick needs selector */}
        <div className={cn(
          "grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto mb-14 transition-all duration-700",
          needsAnimation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}>
        {needsButtons.map((need, index) => (
            <Link
              key={need.href}
              to={need.href}
              className={cn(
                "flex items-center gap-3 p-4 bg-card rounded-xl border border-border/50 hover:border-primary hover:shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.4)] hover:-translate-y-2 transition-all duration-300 group",
                needsAnimation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: needsAnimation.isVisible ? `${200 + index * 80}ms` : "0ms" }}
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <need.icon className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" />
              </div>
              <span className="text-sm font-medium text-foreground/80 group-hover:text-foreground leading-tight">
                {need.label}
              </span>
            </Link>
          ))}
        </div>

        {/* Services grid */}
        <div 
          ref={servicesAnimation.ref as React.RefObject<HTMLDivElement>}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto mb-12"
        >
          {services.map((service, index) => (
            <Link
              key={service.href}
              to={service.href}
              className={cn(
                "group relative block p-6 rounded-2xl border transition-all duration-500 hover:-translate-y-2 overflow-hidden",
                service.highlighted 
                  ? "bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30 hover:border-primary hover:shadow-[0_20px_50px_-15px_hsl(var(--primary)/0.5)]" 
                  : "bg-card border-border/50 hover:border-primary/50 hover:shadow-[0_20px_50px_-15px_hsl(var(--primary)/0.3)]",
                servicesAnimation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: servicesAnimation.isVisible ? `${100 + index * 80}ms` : "0ms" }}
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:via-transparent group-hover:to-secondary/5 transition-all duration-500 pointer-events-none" />
              
              <div
                className={`relative w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 ${
                  service.highlighted
                    ? "bg-primary/20 text-primary group-hover:bg-primary group-hover:text-primary-foreground"
                    : "bg-muted text-primary group-hover:bg-primary group-hover:text-primary-foreground"
                }`}
              >
                <service.icon className="w-6 h-6 transition-transform duration-300 group-hover:rotate-6" />
              </div>

              <h3 className="relative text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
                {service.title}
              </h3>
              <p className="relative text-muted-foreground text-sm leading-relaxed mb-3">
                {service.description}
              </p>
              <span className="relative inline-flex items-center text-primary text-sm font-medium transition-all duration-300">
                Ver más
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-2 transition-transform duration-300" />
              </span>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className={cn(
          "text-center transition-all duration-700",
          servicesAnimation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}
        style={{ transitionDelay: servicesAnimation.isVisible ? "500ms" : "0ms" }}
        >
          <Button variant="gold" size="lg" className="group" asChild>
            <Link to="/servicios">
              Ver todas las soluciones
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default NewServices;
